import BigNumber from 'bignumber.js';
import { z } from 'zod';
import { DiscountType } from '~/types/document-builder';
import { naturaValues } from '~/types/invoicing';
import { isDefined } from '~/types/typeGuards';
import { parseValue } from '~/utils/numberInputs';

const substitutions: Record<string, string> = {
  ä: 'ae',
  ö: 'oe',
  ü: 'ue',
  ß: 'ss',
  à: 'a',
  á: 'a',
  â: 'a',
  ã: 'a',
  å: 'a',
  ā: 'a',
  è: 'e',
  é: 'e',
  ê: 'e',
  ë: 'e',
  ē: 'e',
  ì: 'i',
  í: 'i',
  î: 'i',
  ï: 'i',
  ī: 'i',
  ò: 'o',
  ó: 'o',
  ô: 'o',
  õ: 'o',
  ø: 'o',
  ō: 'o',
  ù: 'u',
  ú: 'u',
  û: 'u',
  ū: 'u',
  ç: 'c',
  ñ: 'n',
  ś: 's',
  ý: 'y',
  ÿ: 'y',
  '‘': "'",
  '’': "'",
  '“': '"',
  '”': '"',
  '–': '-',
  '—': '-',
};

// eslint-disable-next-line no-control-regex
const nonLatin1 = /[^\x00-\xFF]/g;
// eslint-disable-next-line no-control-regex
const nonPrintableCharactersRegex = /[\u0000-\u001F\u007F]/g;

export function cleanCharacters(input: string): string {
  if (!input) {
    return input;
  }

  const replaced = input
    .split('')
    .map((char) => substitutions[char] || char)
    .join('');

  // Remove remaining non-Latin-1 characters
  const withoutLatin1 = replaced.replace(nonLatin1, '');
  return withoutLatin1.replaceAll(nonPrintableCharactersRegex, '');
}

const toBigNumber: (defaultValue?: number) => (amountString: string) => BigNumber =
  (defaultValue) => (amountString) => {
    const parsedValue = parseValue(amountString);
    if (isNaN(parsedValue) && isDefined(defaultValue)) {
      return BigNumber(defaultValue);
    }
    return BigNumber(parsedValue);
  };

const nonNegative: (number: BigNumber) => BigNumber = (number) => BigNumber.max(number, 0);
const max: (max: number) => (number: BigNumber) => BigNumber = (max) => (number) => BigNumber.min(number, max);

const quantitySchema = z.string().transform(toBigNumber(0)).transform(nonNegative);

export const priceSchema = z.string().transform(toBigNumber(0));

export const parseBigNumber = (defaultValue: number) => (value: string) =>
  z.string().transform(toBigNumber(defaultValue)).parse(value);

const discountSchema = z
  .object({
    value: z.string(),
    type: z.nativeEnum(DiscountType),
  })
  .transform((data) => {
    const transformedValue = nonNegative(toBigNumber(0)(data.value));

    if (data.type === DiscountType.Amount) {
      return {
        type: data.type,
        value: transformedValue,
      };
    }

    return {
      type: data.type,
      value: max(100)(transformedValue),
    };
  });
export const percentageSchema = z.string().transform(toBigNumber());

export const latin1Schema = z.string().transform(cleanCharacters);

const managementDataSchema = z.object({
  dataType: z.string().nullable(),
  text: z.string().nullable(),
  date: z.date().nullable(),
  number: z.string().nullable(),
});

const articleCodeSchema = z.object({
  type: z.string(),
  value: latin1Schema,
});

export const lineSchema = z.object({
  code: latin1Schema,
  articleCodes: z.array(articleCodeSchema).default([]),
  description: latin1Schema,
  extendedDescription: z.string().nullable(),
  discount: discountSchema,
  grossPrice: priceSchema,
  netPrice: priceSchema,
  quantity: quantitySchema,
  unit: latin1Schema,
  vat: z.object({ percentage: percentageSchema, taxExemption: z.enum(naturaValues).nullable() }),
  managementData: z.array(managementDataSchema),
});
